import React, { useState } from 'react';
import { createUserWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { Field, InlineAlert, PageHeader, SectionHeading } from '../../components/ui/Primitives';
import { getSecondaryAuth } from '../../firebase';
import { addUserProfileFs } from '../../utils/firestoreStore';

export default function AdminSignup() {
  const useFirebase = process.env.REACT_APP_USE_FIREBASE === 'true';
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleCreateAdmin = async (event) => {
    event.preventDefault();
    setError('');
    setSuccess('');

    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim() || form.password.length < 6) {
      setError('Complete every field and use a password of at least six characters.');
      return;
    }

    if (!useFirebase) {
      setError('Account creation is unavailable because the Firebase connection is disabled.');
      return;
    }

    setSaving(true);
    try {
      const secondaryAuth = getSecondaryAuth();
      const credential = await createUserWithEmailAndPassword(
        secondaryAuth,
        form.email.trim(),
        form.password
      );

      await updateProfile(credential.user, {
        displayName: `${form.firstName} ${form.lastName}`
      });

      await addUserProfileFs(credential.user.uid, {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        role: 'admin',
        roles: ['admin'],
        activeRole: 'admin',
        lmsActive: true,
        avatar: `${form.firstName?.[0] || ''}${form.lastName?.[0] || ''}`.toUpperCase()
      });

      await signOut(secondaryAuth);

      setSuccess(`Administrator account created for ${form.email.trim()}.`);
      setForm({
        firstName: '',
        lastName: '',
        email: '',
        password: ''
      });
    } catch (err) {
      console.error('Failed to create administrator account', err);
      setError(err?.code === 'auth/email-already-in-use'
        ? 'An account already exists for this email address.'
        : 'The administrator account could not be created. Verify the details and try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader eyebrow="Advanced" title="Administrator access" description="Create a dedicated administrator sign-in without interrupting your current session." />
      <form className="focused-form" onSubmit={handleCreateAdmin}>
        <SectionHeading title="New administrator" description="All fields are required. Share the temporary password through a secure channel." />
        {error && <InlineAlert tone="danger">{error}</InlineAlert>}
        {success && <InlineAlert tone="success">{success}</InlineAlert>}
        <section className="focused-form-section">
          <h3>Account details</h3>
          <div className="ui-form-grid">
            <Field label="First name"><input type="text" autoComplete="given-name" value={form.firstName} onChange={event => updateField('firstName', event.target.value)} /></Field>
            <Field label="Last name"><input type="text" autoComplete="family-name" value={form.lastName} onChange={event => updateField('lastName', event.target.value)} /></Field>
            <Field label="Email"><input type="email" autoComplete="email" value={form.email} onChange={event => updateField('email', event.target.value)} /></Field>
            <Field label="Temporary password" hint="Use at least six characters."><input type="password" autoComplete="new-password" value={form.password} onChange={event => updateField('password', event.target.value)} /></Field>
          </div>
        </section>
        <div className="ui-form-actions"><button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Creating…' : 'Create administrator'}</button></div>
      </form>
    </div>
  );
}
